/* eslint-disable */
import React, { useRef, useState, useCallback } from 'react';
import './index.less';
import './index.en.less';

export interface IIconCardItem {
  icon: React.ReactNode;
  title: string;
  content: string;
}

export interface IIconCardCarouselProps {
  mainTitle?: string;
  subTitle?: string;
  description?: string;
  illustration?: React.ReactNode;
  items?: IIconCardItem[];
  id?: string;
  defaultActive?: number;
  /** top: 標題區左文右圖，卡片橫排於下方；side: 插圖位於卡片左側 */
  layout?: 'side' | 'top';
  onComplete?: () => void;
}

const IconCardCarousel: React.FunctionComponent<IIconCardCarouselProps> = ({
  mainTitle,
  subTitle,
  description,
  illustration,
  items = [],
  id,
  defaultActive = 0,
  layout = 'side',
  onComplete,
}) => {
  const [activeIndices, setActiveIndices] = useState<Set<number>>(
    () => new Set(items.map((_, index) => index))
  );
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartX = useRef(0);
  const scrollLeft = useRef(0);
  const hasMoved = useRef(false);
  const completedRef = useRef(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  React.useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState, items.length]);

  const scrollByDirection = (dir: 1 | -1) => {
    const el = scrollContainerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: 'smooth' });
    if (!completedRef.current) {
      completedRef.current = true;
      onComplete?.();
    }
  };

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!scrollContainerRef.current) return;
    setIsDragging(true);
    dragStartX.current = e.pageX - scrollContainerRef.current.offsetLeft;
    scrollLeft.current = scrollContainerRef.current.scrollLeft;
    hasMoved.current = false;
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging || !scrollContainerRef.current) return;
      e.preventDefault();
      const x = e.pageX - scrollContainerRef.current.offsetLeft;
      const walk = (x - dragStartX.current) * 1.5;
      scrollContainerRef.current.scrollLeft = scrollLeft.current - walk;
      if (Math.abs(walk) > 5) {
        hasMoved.current = true;
      }
    },
    [isDragging]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    if (!completedRef.current && hasMoved.current) {
      completedRef.current = true;
      onComplete?.();
    }
  }, [onComplete]);

  const handleMouseLeave = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
    }
  }, [isDragging]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!scrollContainerRef.current) return;
    dragStartX.current =
      e.touches[0].pageX - scrollContainerRef.current.offsetLeft;
    scrollLeft.current = scrollContainerRef.current.scrollLeft;
    hasMoved.current = false;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!scrollContainerRef.current) return;
    const x = e.touches[0].pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - dragStartX.current) * 1.5;
    scrollContainerRef.current.scrollLeft = scrollLeft.current - walk;
    if (Math.abs(walk) > 5) {
      hasMoved.current = true;
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!completedRef.current && hasMoved.current) {
      completedRef.current = true;
      onComplete?.();
    }
  }, [onComplete]);

  return (
    <div
      className={`icon-card-carousel${
        layout === 'top' ? ' icc-layout-top' : ''
      }`}
      id={id}
    >
      <div className="icc-header">
        <div className="icc-header-text">
          {mainTitle && <h2 className="icc-main-title">{mainTitle}</h2>}
          {subTitle && <h3 className="icc-sub-title">{subTitle}</h3>}
          {description && <p className="icc-description">{description}</p>}
        </div>
        {layout === 'top' && illustration && (
          <div className="icc-header-illustration">{illustration}</div>
        )}
      </div>
      <div className="icc-content-wrapper">
        {layout !== 'top' && illustration && (
          <div className="icc-left-illustration">{illustration}</div>
        )}
        <div className="icc-scroll-area">
        <div
          ref={scrollContainerRef}
          className={`icc-cards-scroll${isDragging ? ' is-dragging' : ''}`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="icc-cards-container">
            {items.map((item, index) => {
              const isActive = activeIndices.has(index);
              const handleCardClick = () => {
                if (hasMoved.current) return;
                setActiveIndices((prev) => {
                  const next = new Set(prev);
                  next.add(index);
                  return next;
                });
              };
              return (
                <div
                  key={index}
                  className={`icc-card${isActive ? ' is-active' : ''}`}
                  onClick={handleCardClick}
                >
                  <div className="icc-card-icon">{item.icon}</div>
                  <h4 className="icc-card-title">{item.title}</h4>
                  <div className="icc-card-content">
                    <p className="icc-card-text">{item.content}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {canScrollLeft && (
          <button
            type="button"
            className="icc-scroll-btn icc-scroll-btn-left"
            aria-label="Scroll left"
            onClick={() => scrollByDirection(-1)}
          >
            <svg
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12.5 15L7.5 10L12.5 5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
        {canScrollRight && (
          <button
            type="button"
            className={`icc-scroll-btn icc-scroll-btn-right${
              !canScrollLeft ? ' icc-scroll-hint' : ''
            }`}
            aria-label="Scroll right"
            onClick={() => scrollByDirection(1)}
          >
            <svg
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.5 15L12.5 10L7.5 5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(IconCardCarousel);
